import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendDown,
  faArrowTrendUp,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { OverviewStats } from "@/services/api/guardiansv2/types";
import { formatPercent, passRateColor, STATS_KPI_CLASS } from "./chartTheme";

export interface KpiCardsProps {
  overview: OverviewStats | null;
  loading: boolean;
}

function DeltaBadge({ value, invert = false }: { value: number; invert?: boolean }) {
  if (value === 0) return null;
  const positive = invert ? value < 0 : value > 0;
  const icon = positive ? faArrowTrendUp : faArrowTrendDown;
  const color = positive ? "text-emerald-400" : "text-red-400";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono tabular-nums ${color}`}>
      <FontAwesomeIcon icon={icon} className="h-2.5 w-2.5" />
      {Math.abs(value)}
    </span>
  );
}

function KpiCard({
  label,
  value,
  delta,
  deltaInvert,
  valueClassName = "text-slate-100",
}: {
  label: string;
  value: string;
  delta?: number;
  deltaInvert?: boolean;
  valueClassName?: string;
}) {
  return (
    <div
      className={`px-3 py-2.5 ${STATS_KPI_CLASS}`}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={`text-lg font-semibold font-mono tabular-nums ${valueClassName}`}>{value}</span>
        {delta !== undefined ? <DeltaBadge value={delta} invert={deltaInvert} /> : null}
      </div>
    </div>
  );
}

export function KpiCards({ overview, loading }: KpiCardsProps) {
  if (loading && !overview) {
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={`h-16 animate-pulse ${STATS_KPI_CLASS}`} />
        ))}
      </div>
    );
  }
  if (!overview) {
    return (
      <p className="text-sm text-slate-400">
        No screening data in this period. Check alert screening is enabled in Settings.
      </p>
    );
  }

  const passRate = 100 - overview.current.failure_rate * 100;
  const passColor =
    passRateColor(passRate) === "emerald"
      ? "text-emerald-400"
      : passRateColor(passRate) === "amber"
        ? "text-amber-400"
        : "text-red-400";

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
      <KpiCard label="Screenings" value={String(overview.current.total)} delta={overview.delta.screenings} />
      <KpiCard
        label="Pass rate"
        value={formatPercent(passRate)}
        delta={overview.delta.pass_rate}
        valueClassName={passColor}
      />
      <KpiCard
        label="Alerts raised"
        value={String(overview.current.raised_alert)}
        delta={overview.delta.alerts_raised}
        deltaInvert
        valueClassName="text-red-300"
      />
      <KpiCard label="Open alerts" value={String(overview.open_alerts)} valueClassName="text-amber-300" />
      <KpiCard
        label="Failing rules"
        value={String(overview.distinct_failed_rules)}
        valueClassName="text-indigo-200"
      />
    </div>
  );
}
